// src/pages/modules/Dashboard/Dashboard.tsx
import React from 'react'
import {
  Badge, Button, Card, Col, Form, Row, Spinner, Table,
} from 'react-bootstrap'
import { dbRead } from '../../../_services/firebase'
import { formatCurrency } from '../../../_utils'
import type { CustomerReg, LoanData, InstallmentItem } from '../../../_types'
import { PATH } from '../../../_services/customer.service'
import { DB_BAD_DEBT, DB_LOAN, DB_PAYIN, DB_PAYOUT } from '../../../_config/db_paths'

// ─── Types ────────────────────────────────────────────────────
interface StatCard {
  label: string
  value: string
  sub?: string
  color: string
  icon: string
}

type DateMode = 'today' | 'week' | 'month' | 'year' | 'pick_month' | 'pick_day' | 'pick_year' | 'custom'

// ─── Helpers ─────────────────────────────────────────────────
const money = (n: number) => formatCurrency(Math.round(n))

function startOfDay(ts: number) {
  const d = new Date(ts); d.setHours(0,0,0,0); return d.getTime()
}
function endOfDay(ts: number) {
  const d = new Date(ts); d.setHours(23,59,59,999); return d.getTime()
}
function startOfMonth(ts: number) {
  const d = new Date(ts); d.setDate(1); d.setHours(0,0,0,0); return d.getTime()
}
function endOfMonth(ts: number) {
  const d = new Date(ts)
  d.setMonth(d.getMonth()+1); d.setDate(0); d.setHours(23,59,59,999)
  return d.getTime()
}
function startOfYear(ts: number) {
  const d = new Date(ts); d.setMonth(0,1); d.setHours(0,0,0,0); return d.getTime()
}
function endOfYear(ts: number) {
  const d = new Date(ts); d.setMonth(11,31); d.setHours(23,59,59,999); return d.getTime()
}
function tsFromDateStr(s: string) {
  return new Date(s).getTime()
}
function toDateInput(ts: number) {
  return new Date(ts).toISOString().slice(0, 10)
}

// ─── StatCard UI ──────────────────────────────────────────────
function StatBox({ s }: { s: StatCard }) {
  return (
    <Card className='shadow-sm h-100' style={{ borderLeft: `4px solid ${s.color}` }}>
      <Card.Body style={{ padding: '14px 16px' }}>
        <div className='d-flex justify-content-between align-items-start'>
          <div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{s.sub}</div>}
          </div>
          <i className={`${s.icon}`} style={{ fontSize: 22, color: s.color, opacity: 0.4 }} />
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── Mini bar chart (pure CSS) ────────────────────────────────
function MiniBarChart({ data, color = '#ffc107' }: {
  data: { label: string; value: number }[]
  color?: string
}) {
  const max = Math.max(...data.map((d) => d.value), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div
            style={{
              width: '100%', borderRadius: '3px 3px 0 0',
              backgroundColor: color,
              height: `${Math.max(4, (d.value / max) * 68)}px`,
              transition: 'height 0.3s',
            }}
            title={`${d.label}: ${money(d.value)}`}
          />
          <div style={{ fontSize: 9, color: '#888', textAlign: 'center', lineHeight: 1.2 }}>{d.label}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Dashboard ────────────────────────────────────────────────
export default function Dashboard() {
  const [loading,    setLoading]    = React.useState(true)
  const [dateMode,   setDateMode]   = React.useState<DateMode>('month')
  const [pickMonth,  setPickMonth]  = React.useState('')
  const [pickDay,    setPickDay]    = React.useState('')
  const [pickYear,   setPickYear]   = React.useState('')
  const [customFrom, setCustomFrom] = React.useState(toDateInput(startOfMonth(Date.now())))
  const [customTo,   setCustomTo]   = React.useState(toDateInput(Date.now()))

  // Raw data
  const [allLoans,    setAllLoans]    = React.useState<Record<string, LoanData>>({})
  const [allPayin,    setAllPayin]    = React.useState<Record<string, Record<string, InstallmentItem>>>({})
  const [allPayout,   setAllPayout]   = React.useState<Record<string, any>>({})
  const [allCustomers,setAllCustomers]= React.useState<CustomerReg[]>([])
  const [allBadDebt,  setAllBadDebt]  = React.useState<Record<string, any>>({})

  // ─── Load ────────────────────────────────────────────────────
  React.useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const [loanRaw, payinRaw, payoutRaw, bdRaw,
               ca, cb, cc, cd, ce] = await Promise.all([
          dbRead<Record<string, LoanData>>(DB_LOAN),
          dbRead<any>(DB_PAYIN),
          dbRead<any>(DB_PAYOUT),
          dbRead<any>(DB_BAD_DEBT),
          dbRead<Record<string, CustomerReg>>(PATH.regAll),
          dbRead<Record<string, CustomerReg>>(PATH.loanAll),
          dbRead<Record<string, CustomerReg>>(PATH.activeAll),
          dbRead<Record<string, CustomerReg>>(PATH.closedAll),
          dbRead<Record<string, CustomerReg>>(PATH.badDebtAll),
        ])

        setAllLoans(loanRaw || {})
        setAllPayin(payinRaw || {})
        setAllPayout(payoutRaw || {})
        setAllBadDebt(bdRaw || {})

        const combined = [
          ...Object.values(ca || {}),
          ...Object.values(cb || {}),
          ...Object.values(cc || {}),
          ...Object.values(cd || {}),
          ...Object.values(ce || {}),
        ]
        setAllCustomers(combined)
      } catch(e: any) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // ─── Available dates ─────────────────────────────────────────
  const availableDates = React.useMemo(() => {
    const months = new Set<string>()
    const days   = new Set<string>()
    const years  = new Set<string>()
    Object.values(allPayout).forEach((p: any) => {
      const ts = p.createdAt || p.updatedAt || 0
      if (!ts) return
      const d = new Date(ts)
      months.add(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`)
      days.add(d.toISOString().slice(0,10))
      years.add(String(d.getFullYear()))
    })
    Object.values(allPayin).forEach((installs: any) => {
      Object.values(installs || {}).forEach((ins: any) => {
        const ts = ins.paidAt || 0; if (!ts) return
        const d = new Date(ts)
        months.add(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`)
        days.add(d.toISOString().slice(0,10))
        years.add(String(d.getFullYear()))
      })
    })
    return {
      months: Array.from(months).sort().reverse(),
      days:   Array.from(days).sort(),
      years:  Array.from(years).sort().reverse(),
    }
  }, [allPayout, allPayin])

  // ─── Date range from mode ─────────────────────────────────────
  const { fromTs, toTs } = React.useMemo(() => {
    const now = Date.now()
    switch (dateMode) {
      case 'today':  return { fromTs: startOfDay(now),   toTs: endOfDay(now) }
      case 'week': {
        const d = new Date(); d.setDate(d.getDate() - 6); d.setHours(0,0,0,0)
        return { fromTs: d.getTime(), toTs: endOfDay(now) }
      }
      case 'month':  return { fromTs: startOfMonth(now), toTs: endOfMonth(now) }
      case 'year':   return { fromTs: startOfYear(now),  toTs: endOfYear(now) }
      case 'pick_month': {
        if (!pickMonth) return { fromTs: startOfMonth(now), toTs: endOfMonth(now) }
        const ts = new Date(pickMonth + '-01').getTime()
        return { fromTs: startOfMonth(ts), toTs: endOfMonth(ts) }
      }
      case 'pick_day': {
        if (!pickDay) return { fromTs: startOfDay(now), toTs: endOfDay(now) }
        const ts = tsFromDateStr(pickDay)
        return { fromTs: startOfDay(ts), toTs: endOfDay(ts) }
      }
      case 'pick_year': {
        if (!pickYear) return { fromTs: startOfYear(now), toTs: endOfYear(now) }
        const ts = new Date(`${pickYear}-01-01`).getTime()
        return { fromTs: startOfYear(ts), toTs: endOfYear(ts) }
      }
      case 'custom':
      default:
        return {
          fromTs: tsFromDateStr(customFrom) || startOfMonth(now),
          toTs:   endOfDay(tsFromDateStr(customTo) || now),
        }
    }
  }, [dateMode, customFrom, customTo, pickMonth, pickDay, pickYear])

  // ─── Compute stats ────────────────────────────────────────────
  const stats = React.useMemo(() => {
    // ยอดปล่อย (payout createdAt ในช่วง)
    let totalPayout       = 0
    let payoutCount       = 0
    let totalInterest     = 0

    Object.entries(allPayout).forEach(([cid, p]) => {
      const ts = p.createdAt || p.updatedAt || 0
      if (ts >= fromTs && ts <= toTs) {
        totalPayout += p.amount || 0
        payoutCount++
        const l = allLoans[cid]
        if (l) totalInterest += l.interestAmount || 0
      }
    })

    // ยอดเก็บได้จริง (payin paidAt ในช่วง)
    let totalCollected    = 0
    let collectedCount    = 0
    let overdueCount      = 0
    let overdueAmount     = 0

    Object.entries(allPayin).forEach(([_cid, installs]) => {
      Object.values(installs || {}).forEach((ins: any) => {
        const paidAt = ins.paidAt || ins.updatedAt || 0
        if (ins.status === 'paid' || ins.status === 'partial') {
          if (paidAt >= fromTs && paidAt <= toTs) {
            totalCollected += ins.paidAmount || 0
            collectedCount++
          }
        }
        if (ins.status === 'pending') {
          overdueCount++
          overdueAmount += ins.dueAmount || 0
        }
      })
    })

    // ลูกค้าในระบบ
    const activeCount  = allCustomers.filter((c) => c.meta.status === 'paid').length
    const pendingCount = allCustomers.filter((c) => c.meta.status === 'pending' || c.meta.status === 'รอการตรวจสอบ').length
    const waitingCount = allCustomers.filter((c) => c.meta.status === 'waiting').length
    const badDebtCount = Object.keys(allBadDebt).length
    const totalCustomers = allCustomers.length

    // ยอดรวมคงค้างทั้งหมด (ลูกค้า active ทุกคน)
    let totalOutstanding = 0
    Object.entries(allLoans).forEach(([cid, l]) => {
      const cust = allCustomers.find((c) => c.key === cid)
      if (!cust || cust.meta.status !== 'paid') return
      const installs = allPayin[cid] || {}
      const paid = Object.values(installs).reduce((s: number, ins: any) => s + (ins.paidAmount || 0), 0)
      totalOutstanding += Math.max(0, (l.totalWithInterest || 0) - paid)
    })

    // ยอดดอกที่คาดว่าจะได้รับทั้งหมด (active loans)
    let totalExpectedInterest = 0
    Object.entries(allLoans).forEach(([cid, l]) => {
      const cust = allCustomers.find((c) => c.key === cid)
      if (!cust || cust.meta.status !== 'paid') return
      totalExpectedInterest += l.interestAmount || 0
    })

    // Daily breakdown (last 7 days for chart)
    const dailyCollect: { label: string; value: number }[] = []
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i)
      const dayStart = startOfDay(d.getTime())
      const dayEnd   = endOfDay(d.getTime())
      let sum = 0
      Object.values(allPayin).forEach((installs: any) => {
        Object.values(installs || {}).forEach((ins: any) => {
          const paidAt = ins.paidAt || 0
          if (paidAt >= dayStart && paidAt <= dayEnd) sum += ins.paidAmount || 0
        })
      })
      const label = `${d.getDate()}/${d.getMonth()+1}`
      dailyCollect.push({ label, value: sum })
    }

    const dailyPayout: { label: string; value: number }[] = []
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i)
      const dayStart = startOfDay(d.getTime())
      const dayEnd   = endOfDay(d.getTime())
      let sum = 0
      Object.values(allPayout).forEach((p: any) => {
        const ts = p.createdAt || 0
        if (ts >= dayStart && ts <= dayEnd) sum += p.amount || 0
      })
      const label = `${d.getDate()}/${d.getMonth()+1}`
      dailyPayout.push({ label, value: sum })
    }

    // Top overdue customers
    const overdueList: { name: string; amount: number; overdue: number; zone: string }[] = []
    Object.entries(allPayin).forEach(([cid, installs]) => {
      const cust = allCustomers.find((c) => c.key === cid)
      if (!cust || cust.meta.status !== 'paid') return
      const overItems = Object.values(installs || {}).filter((ins: any) => ins.status === 'pending')
      if (overItems.length === 0) return
      const overAmt = overItems.reduce((s: number, ins: any) => s + (ins.dueAmount || 0), 0)
      const l = allLoans[cid]
      overdueList.push({
        name:    cust.personal.fullName,
        amount:  overAmt,
        overdue: overItems.length,
        zone:    l?.zone || '-',
      })
    })
    overdueList.sort((a, b) => b.amount - a.amount)

    return {
      totalPayout, payoutCount,
      totalCollected, collectedCount,
      totalInterest,
      overdueCount, overdueAmount,
      activeCount, pendingCount, waitingCount, badDebtCount, totalCustomers,
      totalOutstanding, totalExpectedInterest,
      dailyCollect, dailyPayout,
      overdueList: overdueList.slice(0, 10),
    }
  }, [fromTs, toTs, allLoans, allPayin, allPayout, allBadDebt, allCustomers])

  /* อาจจะแก้ */
  // ─── Stat cards ───────────────────────────────────────────────
  const cards: StatCard[] = [
    { label: 'ยอดปล่อยกู้ (ช่วงที่เลือก)',   value: `${money(stats.totalPayout)} ກີບ`,     sub: `${stats.payoutCount} รายการ`,                   color: '#1565c0', icon: 'fa-solid fa-money-bill-transfer' },
    { label: 'ยอดเก็บได้จริง (ช่วงที่เลือก)', value: `${money(stats.totalCollected)} ກີບ`,  sub: `${stats.collectedCount} งวด`,                   color: '#2e7d32', icon: 'fa-solid fa-hand-holding-dollar' },
    { label: 'ดอกเบี้ยในช่วงที่เลือก',        value: `${money(stats.totalInterest)} ກີບ`,   sub: 'จากการปล่อยกู้ใหม่',                             color: '#f57c00', icon: 'fa-solid fa-percent' },
    { label: 'ยอดค้างชำระ (งวดที่ยังไม่ได้จ่าย)', value: `${money(stats.overdueAmount)} ກີບ`, sub: `${stats.overdueCount} งวด`,                color: '#c62828', icon: 'fa-solid fa-triangle-exclamation' },
    { label: 'ยอดคงค้างรวมทั้งระบบ',          value: `${money(stats.totalOutstanding)} ກີບ`, sub: 'ลูกค้า active ทั้งหมด',                        color: '#6a1b9a', icon: 'fa-solid fa-receipt' },
    { label: 'ดอกเบี้ยที่คาดรับรวม',          value: `${money(stats.totalExpectedInterest)} ກີບ`, sub: 'จาก active loans',                      color: '#00695c', icon: 'fa-solid fa-chart-line' },
  ]

  const customerCards: StatCard[] = [
    { label: 'ลูกค้าทั้งหมดในระบบ', value: `${stats.totalCustomers} คน`,  sub: '',                              color: '#555',    icon: 'fa-solid fa-users' },
    { label: 'กำลังผ่อนชำระ',       value: `${stats.activeCount} คน`,     sub: 'data_c (active)',               color: '#2e7d32', icon: 'fa-solid fa-spinner' },
    { label: 'รอการตรวจสอบ',        value: `${stats.pendingCount} คน`,    sub: 'data_a',                        color: '#f57c00', icon: 'fa-solid fa-clock' },
    { label: 'อนุมัติแล้ว รอจ่าย',  value: `${stats.waitingCount} คน`,    sub: 'data_b',                        color: '#1565c0', icon: 'fa-solid fa-hourglass-half' },
    { label: 'หนี้เสีย',            value: `${stats.badDebtCount} คน`,    sub: 'data_e',                        color: '#c62828', icon: 'fa-solid fa-skull-crossbones' },
  ]

  if (loading) return (
    <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
      <Spinner animation='border' variant='warning' />
      <span className='ms-2 text-muted'>กำลังโหลดข้อมูล...</span>
    </div>
  )

  return (
    <div className='p-3'>
      {/* ─── Header ─────────────────────────────────────────── */}
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>สรุปผลภาพรวม</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>
            {new Date(fromTs).toLocaleDateString('th-TH')} — {new Date(toTs).toLocaleDateString('th-TH')}
          </div>
        </div>
        <Button size='sm' variant='outline-secondary' onClick={() => {
          setLoading(true)
          window.location.reload()
        }}>
          <i className='fa-solid fa-rotate-right me-1' />รีเฟรช
        </Button>
      </div>

      {/* ─── Date mode selector ──────────────────────────────── */}
      <Card className='mb-3 shadow-sm'>
        <Card.Body style={{ padding: '10px 14px' }}>
          <div className='d-flex gap-2 flex-wrap align-items-center'>
            {([
              { key: 'today',      label: 'วันนี้'    },
              { key: 'week',       label: '7 วัน'    },
              { key: 'month',      label: 'เดือนนี้'  },
              { key: 'year',       label: 'ปีนี้'     },
              { key: 'pick_month', label: 'รายเดือน' },
              { key: 'pick_day',   label: 'เลือกวัน' },
              { key: 'pick_year',  label: 'รายปี'    },
              { key: 'custom',     label: 'กำหนดเอง' },
            ] as { key: DateMode; label: string }[]).map((m) => (
              <Button key={m.key} size='sm'
                variant={dateMode === m.key ? 'warning' : 'outline-secondary'}
                onClick={() => setDateMode(m.key)}
              >{m.label}</Button>
            ))}
          </div>

          {/* รายเดือน */}
          {dateMode === 'pick_month' && (
            <div className='mt-2 d-flex align-items-center gap-2 flex-wrap'>
              <span style={{ fontSize: 12, color: '#888' }}>เลือกเดือน:</span>
              <Form.Select size='sm' style={{ width: 180 }}
                value={pickMonth} onChange={(e) => setPickMonth(e.target.value)}>
                <option value=''>-- เลือกเดือน --</option>
                {availableDates.months.map((m) => {
                  const [y, mo] = m.split('-')
                  const th = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.',
                              'ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'][Number(mo)-1]
                  return <option key={m} value={m}>{th} {Number(y)+543}</option>
                })}
              </Form.Select>
              <span style={{ fontSize: 11, color: '#aaa' }}>{availableDates.months.length} เดือนที่มีข้อมูล</span>
            </div>
          )}

          {/* เลือกวัน */}
          {dateMode === 'pick_day' && (
            <div className='mt-2 d-flex align-items-center gap-2 flex-wrap'>
              <span style={{ fontSize: 12, color: '#888' }}>เลือกวัน:</span>
              <Form.Control type='date' size='sm' style={{ width: 160 }}
                value={pickDay}
                min={availableDates.days[0] || ''}
                max={availableDates.days[availableDates.days.length-1] || ''}
                onChange={(e) => setPickDay(e.target.value)}
              />
              {pickDay && (
                <Badge bg={availableDates.days.includes(pickDay) ? 'primary' : 'secondary'}
                  style={{ fontSize: 11 }}>
                  {availableDates.days.includes(pickDay) ? '● มีข้อมูล' : '○ ไม่มีข้อมูล'}
                </Badge>
              )}
              <span style={{ fontSize: 11, color: '#aaa' }}>
                มีข้อมูล {availableDates.days.length} วัน
              </span>
            </div>
          )}

          {/* รายปี */}
          {dateMode === 'pick_year' && (
            <div className='mt-2 d-flex align-items-center gap-2 flex-wrap'>
              <span style={{ fontSize: 12, color: '#888' }}>เลือกปี:</span>
              <Form.Select size='sm' style={{ width: 150 }}
                value={pickYear} onChange={(e) => setPickYear(e.target.value)}>
                <option value=''>-- เลือกปี --</option>
                {availableDates.years.map((y) => (
                  <option key={y} value={y}>พ.ศ. {Number(y)+543}</option>
                ))}
              </Form.Select>
              <span style={{ fontSize: 11, color: '#aaa' }}>{availableDates.years.length} ปีที่มีข้อมูล</span>
            </div>
          )}

          {/* กำหนดเอง */}
          {dateMode === 'custom' && (
            <div className='mt-2 d-flex align-items-center gap-2 flex-wrap'>
              <Form.Control type='date' size='sm' style={{ width: 140 }}
                value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <span style={{ fontSize: 13 }}>ถึง</span>
              <Form.Control type='date' size='sm' style={{ width: 140 }}
                value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ─── Financial stats ─────────────────────────────────── */}
      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>
        💰 สถิติการเงิน
      </div>
      <Row className='g-3 mb-4'>
        {cards.map((s, i) => (
          <Col key={i} xs={6} md={4} lg={2}>
            <StatBox s={s} />
          </Col>
        ))}
      </Row>

      {/* ─── Customer stats ───────────────────────────────────── */}
      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>
        👥 สถิติลูกค้า
      </div>
      <Row className='g-3 mb-4'>
        {customerCards.map((s, i) => (
          <Col key={i} xs={6} md={4} lg={2}>
            <StatBox s={s} />
          </Col>
        ))}
      </Row>

      {/* ─── Charts ──────────────────────────────────────────── */}
      <Row className='g-3 mb-4'>
        <Col xs={12} md={6}>
          <Card className='shadow-sm h-100'>
            <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
              <i className='fa-solid fa-arrow-trend-up me-2 text-success' />
              ยอดเก็บเงิน 7 วันล่าสุด (กีบ)
            </Card.Header>
            <Card.Body>
              <MiniBarChart data={stats.dailyCollect} color='#2e7d32' />
              <div className='mt-2 text-end' style={{ fontSize: 11, color: '#888' }}>
                รวม 7 วัน: <strong>{money(stats.dailyCollect.reduce((s, d) => s + d.value, 0))} กีบ</strong>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className='shadow-sm h-100'>
            <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
              <i className='fa-solid fa-arrow-trend-down me-2 text-primary' />
              ยอดปล่อยกู้ 7 วันล่าสุด (กีบ)
            </Card.Header>
            <Card.Body>
              <MiniBarChart data={stats.dailyPayout} color='#1565c0' />
              <div className='mt-2 text-end' style={{ fontSize: 11, color: '#888' }}>
                รวม 7 วัน: <strong>{money(stats.dailyPayout.reduce((s, d) => s + d.value, 0))} กีบ</strong>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ─── Summary comparison card ──────────────────────────── */}
      <Row className='g-3 mb-4'>
        <Col xs={12} md={6}>
          <Card className='shadow-sm' style={{ borderColor: '#ffc107' }}>
            <Card.Header style={{ fontSize: 13, fontWeight: 700, backgroundColor: '#fffbf0' }}>
              📊 สรุปเปรียบเทียบ (ช่วงที่เลือก)
            </Card.Header>
            <Card.Body>
              <Table size='sm' style={{ fontSize: 13, marginBottom: 0 }}>
                <tbody>
                  <tr>
                    <td style={{ color: '#555' }}>ยอดปล่อยกู้</td>
                    <td className='text-end' style={{ fontWeight: 700, color: '#1565c0' }}>{money(stats.totalPayout)} ກີບ</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#555' }}>ยอดเก็บได้</td>
                    <td className='text-end' style={{ fontWeight: 700, color: '#2e7d32' }}>{money(stats.totalCollected)} ກີບ</td>
                  </tr>
                  <tr style={{ borderTop: '2px solid #ffc107' }}>
                    <td style={{ fontWeight: 700 }}>กำไร/ขาดทุน (เก็บ - ปล่อย)</td>
                    <td className='text-end' style={{
                      fontWeight: 800,
                      color: stats.totalCollected - stats.totalPayout >= 0 ? '#2e7d32' : '#c62828',
                    }}>
                      {stats.totalCollected - stats.totalPayout >= 0 ? '+' : ''}
                      {money(stats.totalCollected - stats.totalPayout)} ກີບ
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#555' }}>อัตราส่วนเก็บ/ปล่อย</td>
                    <td className='text-end' style={{ fontWeight: 700 }}>
                      {stats.totalPayout > 0
                        ? `${((stats.totalCollected / stats.totalPayout) * 100).toFixed(1)}%`
                        : '-'
                      }
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#555' }}>ยอดค้างชำระ (งวด)</td>
                    <td className='text-end' style={{ fontWeight: 700, color: '#c62828' }}>
                      {money(stats.overdueAmount)} ກີບ ({stats.overdueCount} งวด)
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card className='shadow-sm'>
            <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
              📋 สัดส่วนลูกค้า
            </Card.Header>
            <Card.Body>
              {[
                { label: 'กำลังผ่อนชำระ',  count: stats.activeCount,  color: '#2e7d32' },
                { label: 'รอจ่ายเงิน',      count: stats.waitingCount, color: '#1565c0' },
                { label: 'รอตรวจสอบ',       count: stats.pendingCount, color: '#f57c00' },
                { label: 'หนี้เสีย',        count: stats.badDebtCount, color: '#c62828' },
              ].map((item) => {
                const pct = stats.totalCustomers > 0
                  ? ((item.count / stats.totalCustomers) * 100).toFixed(1)
                  : '0'
                return (
                  <div key={item.label} className='mb-2'>
                    <div className='d-flex justify-content-between' style={{ fontSize: 12, marginBottom: 2 }}>
                      <span>{item.label}</span>
                      <span style={{ fontWeight: 700 }}>{item.count} คน ({pct}%)</span>
                    </div>
                    <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: item.color,
                        borderRadius: 4,
                        transition: 'width 0.5s',
                      }} />
                    </div>
                  </div>
                )
              })}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ─── Top overdue table ────────────────────────────────── */}
      {stats.overdueList.length > 0 && (
        <Card className='shadow-sm mb-3'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
            <i className='fa-solid fa-triangle-exclamation me-2 text-danger' />
            ลูกค้าค้างชำระสูงสุด 10 อันดับ
          </Card.Header>
          <Card.Body style={{ padding: 0 }}>
            <Table size='sm' hover style={{ fontSize: 12, marginBottom: 0 }}>
              <thead className='table-light'>
                <tr>
                  <th>#</th>
                  <th>ชื่อ</th>
                  <th>โซน</th>
                  <th className='text-end'>ยอดค้าง</th>
                  <th className='text-end'>จำนวนงวด</th>
                </tr>
              </thead>
              <tbody>
                {stats.overdueList.map((row, i) => (
                  <tr key={i}>
                    <td style={{ color: '#888' }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{row.name}</td>
                    <td>
                      <Badge bg='secondary' style={{ fontSize: 10 }}>{row.zone}</Badge>
                    </td>
                    <td className='text-end' style={{ color: '#c62828', fontWeight: 700 }}>
                      {money(row.amount)} ກີບ
                    </td>
                    <td className='text-end'>
                      <Badge bg='danger' style={{ fontSize: 10 }}>{row.overdue} งวด</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}